import { useEffect, useState } from "react";
import { Alert, Button, Collapse, OverlayTrigger, Tooltip } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiAlertCircle, mdiCheckboxBlankCircleOutline, mdiCheckboxMarkedCircleOutline, mdiCheckCircle, mdiChevronDown, mdiChevronRight } from "@mdi/js";

import { CriteriaManifestationEntity } from "src/entities/CriteriaManifestationEntity";
import { CriteriaDomainGetItem } from "src/entities/CriteriaDomainEntity";

export type DomainSelections = Record<number, Record<number, number>>;

type DomainsStepProps = {
	manifestations: CriteriaManifestationEntity[];
	domains: CriteriaDomainGetItem[];
	selections: DomainSelections;
	onChange: (selections: DomainSelections) => void;
	onCanAdvanceChange: (canAdvance: boolean) => void;
};

export default function DomainsStep({ manifestations, domains, selections, onChange, onCanAdvanceChange }: DomainsStepProps) {
	const [expandedManifestations, setExpandedManifestations] = useState<Set<number>>(() => new Set(manifestations[0] ? [manifestations[0].id] : []));
	const requiredDomains = domains.filter((domain) => domain.required);
	const requiredDomainsAnswered =
		manifestations.length > 0 && manifestations.every((manifestation) => isManifestationComplete(manifestation.id, selections));

	useEffect(() => {
		setExpandedManifestations(new Set(manifestations[0] ? [manifestations[0].id] : []));
	}, [manifestations]);

	useEffect(() => {
		onCanAdvanceChange(requiredDomainsAnswered);
	}, [requiredDomainsAnswered, onCanAdvanceChange]);

	function isManifestationComplete(manifestationId: number, nextSelections: DomainSelections) {
		return requiredDomains.every((domain) => {
			const selectedOptionId = nextSelections[manifestationId]?.[domain.criteriaDomainId];
			return domain.options.some((option) => option.enabled && option.criteriaDomainOptionId === selectedOptionId);
		});
	}

	function handleToggleManifestation(manifestationId: number) {
		setExpandedManifestations((current) => {
			const next = new Set(current);
			if (next.has(manifestationId)) {
				next.delete(manifestationId);
			} else {
				next.add(manifestationId);
			}
			return next;
		});
	}

	function handleSelect(manifestationId: number, criteriaDomainId: number, criteriaDomainOptionId: number) {
		onChange({
			...selections,
			[manifestationId]: {
				...selections[manifestationId],
				[criteriaDomainId]: criteriaDomainOptionId,
			},
		});
	}

	if (manifestations.length === 0) {
		return <Alert variant="light">Selecione ao menos uma manifestação para preencher os domínios.</Alert>;
	}

	if (domains.length === 0) {
		return <Alert variant="light">Nenhum domínio vinculado ao critério.</Alert>;
	}

	return (
		<div className="d-flex flex-column gap-2 w-100">
			{manifestations.map((manifestation) => {
				const expanded = expandedManifestations.has(manifestation.id);
				const completed = isManifestationComplete(manifestation.id, selections);

				return (
					<div key={manifestation.id}>
						<Button
							type="button"
							variant="light"
							className="d-flex align-items-center justify-content-between rounded-3 border px-3 fw-medium w-100 text-start"
							style={{ minHeight: 40 }}
							onClick={() => handleToggleManifestation(manifestation.id)}
							aria-expanded={expanded}
						>
							<span>{manifestation.title}</span>
							<span className="d-flex align-items-center gap-2">
								<OverlayTrigger overlay={<Tooltip>{completed ? "Todos os campos estão preenchidos" : "Existem campos pendentes"}</Tooltip>}>
									<Icon path={completed ? mdiCheckCircle : mdiAlertCircle} size={0.85} color={completed ? "#198754" : "#b02a37"} />
								</OverlayTrigger>
								<Icon path={expanded ? mdiChevronDown : mdiChevronRight} size={0.8} />
							</span>
						</Button>

						<Collapse in={expanded}>
							<div>
								<div className="d-flex flex-column gap-2 my-3 ms-4">
									{domains.map((domain) => {
										const enabledOptions = domain.options.filter((option) => option.enabled);
										const selectedOptionId = selections[manifestation.id]?.[domain.criteriaDomainId];

										return (
											<div key={domain.criteriaDomainId} className="rounded-3 border p-3">
												<div className="d-flex align-items-center gap-1 fw-medium mb-2">
													{domain.title}
													{domain.required && (
														<OverlayTrigger overlay={<Tooltip>É obrigatório responder</Tooltip>}>
															<span className="text-danger" role="img" aria-label="Domínio obrigatório">
																*
															</span>
														</OverlayTrigger>
													)}
												</div>
												<div className="d-flex flex-column gap-2">
													{enabledOptions.length === 0 ? (
														<div className="small text-muted">Nenhuma opção disponível.</div>
													) : (
														enabledOptions.map((option) => {
															const checked = selectedOptionId === option.criteriaDomainOptionId;

															return (
																<Button
																	key={option.criteriaDomainOptionId}
																	type="button"
																	variant="outline-dark"
																	className={`d-flex align-items-center w-100 gap-3 rounded-3 border text-start ${checked ? "active" : ""}`}
																	onClick={() => handleSelect(manifestation.id, domain.criteriaDomainId, option.criteriaDomainOptionId)}
																>
																	<div>
																		<Icon path={checked ? mdiCheckboxMarkedCircleOutline : mdiCheckboxBlankCircleOutline} size={1} />
																	</div>
																	{option.title}
																</Button>
															);
														})
													)}
												</div>
											</div>
										);
									})}
								</div>
							</div>
						</Collapse>
					</div>
				);
			})}
		</div>
	);
}
